/**
 * Content view component.
 * @module components/theme/View/ContentView
 */

import React from 'react';
import ContentFeedbackBox from '../../../shared/ContentFeedbackBox';

import ContentAside from './components/ContentAsideProgram';
import ContentBody from './components/ContentBody';
import ContentHero from './components/ContentHero';
import ContentMeta from './components/ContentMeta';
import ContentTags from './components/ContentTags';
import ContentSeo from './components/ContentSeo';

import {
  resolveCreatedDate,
  resolveEffectiveDate,
  resolveModifiedDate,
  resolveProgramTitle,
  resolvePublicPageUrl,
  resolveTags,
} from './helpers';

import './ContentView.less';

type Props = {
  content?: any;
};

function ContentViewSkeleton() {
  return (
    <main className="content-view content-view--loading" aria-busy="true">
      <section
        className="content-view__hero content-view__hero--skeleton"
        aria-hidden="true"
      >
        <div className="content-view__hero-inner">
          <div className="content-view__hero-copy">
            <div className="content-view__skeleton content-view__skeleton--badge" />
            <div className="content-view__skeleton content-view__skeleton--title" />
            <div className="content-view__skeleton content-view__skeleton--text" />
            <div className="content-view__skeleton content-view__skeleton--text-short" />
            <div className="content-view__skeleton content-view__skeleton--button" />
          </div>

          <div className="content-view__skeleton content-view__skeleton--cover" />
        </div>
      </section>

      <div className="content-view__layout" aria-hidden="true">
        <div className="content-view__main">
          <section className="content-view__article">
            <div className="content-view__skeleton content-view__skeleton--paragraph" />
            <div className="content-view__skeleton content-view__skeleton--paragraph" />
            <div className="content-view__skeleton content-view__skeleton--paragraph-short" />
          </section>
        </div>

        <aside className="content-view__aside">
          <div className="content-view__aside-section">
            <div className="content-view__aside-section-head">
              <div className="content-view__skeleton content-view__skeleton--aside-title" />
            </div>
            <div className="content-view__aside-item">
              <div className="content-view__skeleton content-view__skeleton--aside-line" />
              <div className="content-view__skeleton content-view__skeleton--aside-line-short" />
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default function ContentView({ content }: Props) {
  const isLoadingContent = !content?.['@id'] && !content?.title;

  if (isLoadingContent) {
    return <ContentViewSkeleton />;
  }

  const tags = resolveTags(content);
  const created = resolveCreatedDate(content);
  const modified = resolveModifiedDate(content);
  const effective = resolveEffectiveDate(content);
  const title = content?.title || '';
  const pageUrl = resolvePublicPageUrl(content);
  const programTitle = resolveProgramTitle(content);

  return (
    <main className="content-view">
      <ContentSeo content={content} />
      <ContentHero content={content} />

      <div className="content-view__layout">
        <div className="content-view__main">
          <section className="content-view__article">
            <ContentBody content={content} />
          </section>

          <ContentTags tags={tags} />

          <ContentMeta
            created={created}
            modified={modified}
            effective={effective}
            programTitle={programTitle}
            pageUrl={pageUrl}
            title={title}
            content={content}
          />

          <ContentFeedbackBox />
        </div>

        <ContentAside content={content} />
      </div>
    </main>
  );
}
